import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAppSelector } from '@/redux/hooks'
import { activeReasonCodes } from '@/domain/return-reasons'
import { returnReasonsApi } from '@/infrastructure/api/returns-and-refunds'
import { Scope } from '@/ui/components/group-settings'
import { ReturnReasonChoice } from '@/ui/components/warehouses/condition-pill'

// The reasons a rule on screen can name: the store's own, or the group's, named as the portal shows them.
export function useReturnReasonChoice(
  scope: Scope,
  enabled: boolean
): ReturnReasonChoice | undefined {
  const { i18n } = useTranslation()
  const copies = useAppSelector((s) => s.settingsApi.copies.response)
  const { data } = useQuery({
    queryKey: ['returnReasonChoice', scope.group],
    queryFn: ({ signal }) => returnReasonsApi.find(scope.group, signal),
    enabled
  })
  const ecommerceId = scope.kind === 'store' ? scope.ecommerceId : undefined
  const reasons = enabled
    ? (ecommerceId && data?.overrides[ecommerceId]) || data?.group
    : undefined
  const language = i18n.language.toUpperCase()
  const labelOf = useCallback(
    (code: string) => copies[`${language}-select_reason_${code}`] ?? '',
    [copies, language]
  )
  return useMemo(
    () => reasons && { codes: activeReasonCodes(reasons), labelOf },
    [reasons, labelOf]
  )
}
